import { Fragment, useMemo } from "react";
import { useLanguage } from "../../../../widgets/LangContext";
import { useSelector } from "react-redux";
import { NumberToNepaliNum } from "../../../../widgets/NumberFormat";
import AppURL from "../../../../widgets/AppURL";

const PrintIssueReturnSummaryReport = ({ listRpt }) => {
  const { language } = useLanguage();
  const { menu_list } = useSelector((state) => state?.software);

  const Org_Details = menu_list?.structure;

  /** GROUP → Faculty → Level */
  const groupedData = useMemo(() => {
    return listRpt.reduce((acc, row) => {
      const faculty = row.faculty || "Unknown Faculty";
      const level = row.level || "Unknown Level";

      acc[faculty] = acc[faculty] || {};
      acc[faculty][level] = acc[faculty][level] || [];

      acc[faculty][level].push(row);
      return acc;
    }, {});
  }, [listRpt]);

  /** Helper: calculate totals */
  const calculateTotals = (rows) =>
    rows.reduce(
      (t, r) => {
        t.total += r.total_books;
        t.issued += r.issued_books;
        t.returned += r.total_returned;
        t.available += r.total_books - r.issued_books;
        return t;
      },
      { total: 0, issued: 0, returned: 0, available: 0 }
    );

  const grandTotal = calculateTotals(listRpt);
  // Now you can use totalDebitAmount wherever you need to display the total debit amount.
  return (
    <Fragment>
      <style>
        {`@media print {
         @page {
          size: A4 portrait;
          page-break-before: always;
          margin: 0.2in; 
          border: 1px solid black;
          }
          }`}
      </style>
      <div className="bg-white h-screen">
        <div className="relative border-x border-t border-slate300 p-1">
          <table className="w-full text-sm text-left rtl:text-right text-black">
            <tbody>
              <tr>
                <td
                  className={`w-[25%] text-[12px] text-black whitespace-nowrap ${
                    language ? "font-public-sans" : "font-sangrila"
                  }`}
                >
                  {language ? "Reg. No.: " : "दर्ता नं.:"}:{" "}
                  {language
                    ? Org_Details?.org_reg
                    : NumberToNepaliNum(Org_Details?.org_reg || "")}
                </td>
                <td className="w-[25%]"></td>
                <td className="w-[25%]"></td>
                <td
                  align="right"
                  className={`w-[25%] text-[12px] text-black whitespace-nowrap ${
                    language ? "font-public-sans" : "font-sangrila"
                  }`}
                >
                  {language ? "Phone: " : "फोन नम्बर:"}{" "}
                  {language
                    ? Org_Details?.org_phone
                    : NumberToNepaliNum(Org_Details?.org_phone || 0)}
                </td>
              </tr>
              <tr>
                <td
                  rowSpan={4}
                  className="w-[25%] font-medium text-black whitespace-nowrap"
                >
                  <img
                    width={60}
                    height={60}
                    src={AppURL.BaseURL + Org_Details?.logo}
                    alt=""
                  />
                </td>
                <td
                  align="center"
                  colSpan={2}
                  className={`w-[25%] text-black font-bold text-[18px] ${
                    language ? "font-public-sans" : "font-sangrila"
                  }`}
                >
                  {language ? Org_Details?.org_name : Org_Details?.org_name_nep}
                </td>
                <td className="w-[25%]"></td>
              </tr>
              <tr>
                <td
                  align="center"
                  colSpan={2}
                  className={`w-[25%] text-black font-normal text-[12px] ${
                    language ? "font-public-sans" : "font-sangrila"
                  }`}
                >
                  {language ? Org_Details?.org_add : Org_Details?.org_add_nep}
                </td>
                <td
                  align="right"
                  className={`w-[25%] text-black font-normal text-[12px] ${
                    language ? "font-public-sans" : "font-sangrila"
                  }`}
                >
                  {language ? "Date: " : "मिति: "} {menu_list?.np_date}
                </td>
              </tr>
              <tr>
                <td
                  align="center"
                  colSpan={2}
                  className={`w-[25%] underline underline-2 text-black font-semibold text-[15px] ${
                    language ? "font-public-sans" : "font-sangrila"
                  }`}
                >
                  {language
                    ? "Issue/Return Summary Report"
                    : "जारी/फिर्ता सारांश रिपोर्ट"}
                </td>
                <td className="w-[25%]"></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="space-y-6 overflow-x-auto">
          {Object.entries(groupedData).map(([faculty, levels]) => {
            const facultyRows = Object.values(levels).flat();
            const facultyTotal = calculateTotals(facultyRows);

            return (
              <div key={faculty} className="border rounded-md">
                {/* FACULTY HEADER */}
                <div className="bg-gray-200 px-4 uppercase py-1 font-bold text-sm">
                  Faculty: {faculty}
                </div>

                {Object.entries(levels).map(([level, rows]) => {
                  const sortedRows = [...rows].sort((a, b) => {
                    const programCompare = a.program.localeCompare(b.program);
                    if (programCompare !== 0) return programCompare;

                    return a.year_semester_id - b.year_semester_id;
                  });
                  const levelTotal = calculateTotals(sortedRows);

                  return (
                    <div key={level} className="p-3">
                      {/* LEVEL HEADER */}
                      <div className="font-semibold text-md mb-2">
                        Level: {level}
                      </div>
                      <table className="min-w-full border text-[12px] mb-3">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="border px-1 py-1 text-left">
                              Program
                            </th>
                            <th className="border px-1 py-1 text-center">
                              Year / Semester
                            </th>
                            <th className="border px-1 py-1 text-right">
                              Total
                            </th>
                            <th className="border px-1 py-1 text-right">
                              Issued
                            </th>
                            <th className="border px-1 py-1 text-right">
                              Returned
                            </th>
                            <th className="border px-1 py-1 text-right">
                              Available
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {sortedRows.map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="border px-1 py-1">
                                {row.program}
                              </td>
                              <td className="border px-1 py-1 text-center">
                                {row.year_semester}
                              </td>
                              <td className="border px-1 py-1 text-right">
                                {row.total_books}
                              </td>
                              <td className="border px-1 py-1 text-right text-blue-600">
                                {row.issued_books}
                              </td>
                              <td className="border px-1 py-1 text-right text-green-600">
                                {row.total_returned}
                              </td>
                              <td className="border px-1 py-1 text-right font-semibold text-purple-600">
                                {row.total_books - row.issued_books}
                              </td>
                            </tr>
                          ))}
                        </tbody>

                        {/* LEVEL TOTAL */}
                        <tfoot>
                          <tr className="bg-gray-100 font-semibold">
                            <td
                              colSpan="2"
                              className="border px-1 py-1 text-right"
                            >
                              Level Total
                            </td>
                            <td className="border px-1 py-1 text-right">
                              {levelTotal.total}
                            </td>
                            <td className="border px-1 py-1 text-right">
                              {levelTotal.issued}
                            </td>
                            <td className="border px-1 py-1 text-right">
                              {levelTotal.returned}
                            </td>
                            <td className="border px-1 py-1 text-right">
                              {levelTotal.available}
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  );
                })}

                {/* FACULTY TOTAL */}
                <div className="bg-gray-100 px-4 py-2 font-semibold">
                  Faculty Total — Total: {facultyTotal.total} | Issued:{" "}
                  {facultyTotal.issued} | Returned: {facultyTotal.returned} |
                  Available: {facultyTotal.available}
                </div>
              </div>
            );
          })}

          {/* GRAND TOTAL */}
          <div className="bg-gray-300 px-2 py-1 font-bold text-md">
            Grand Total — Total: {grandTotal.total} | Issued:{" "}
            {grandTotal.issued} | Returned: {grandTotal.returned} | Available:{" "}
            {grandTotal.available}
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default PrintIssueReturnSummaryReport;
